/**
 * useSubscription — W2.6
 *
 * GET /api/v2/subscription: aktuelles Abo (oder null wenn kein Abo).
 * Mutations: subscribe, cancel, changePlan, updatePaymentMethod.
 * 409-Handling: Doppel-Subscribe zeigt Toast und lädt Status neu.
 */
import { useState, useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '../../lib/api-client'
import { ApiError } from '../../lib/errors/ApiError'
import { apiErrorStatus } from '../../lib/errors/apiErrorMessage'

export type PaymentMethod = {
  brand: string
  last4: string
  exp_month: number
  exp_year: number
}

export type SubscriptionPlan = {
  id: string
  name: string
  price_monthly: number
}

// FIN-PAY-001 — Crypto-Premium (HNT/Solana). Antwort von POST /api/v2/subscription/crypto_quote.
export type CryptoQuote = {
  provider: 'hnt'
  plan_id: string
  duration_months: number
  wallet_address: string
  amount: number
  amount_bones: number
  amount_usd: number
  hnt_price: number
  memo: string
  code: number
  expires_at: string
}

// Kanonische Wallet-QR-URL (Parität zu main payout.js): payee + amount in Bones.
// Das BE liefert die Bausteine; die URL wird hier gebaut (vermeidet &-Escaping im JSON).
export function buildHeliumPaymentUrl(quote: Pick<CryptoQuote, 'wallet_address' | 'amount_bones'>): string {
  return `https://wallet.helium.com/payment?payee=${quote.wallet_address}&amount=${quote.amount_bones}`
}

// Status-Werte gemaess BE (subscriptions_controller#subscription_status):
// 'expired' wird geliefert sobald premium_expires_at in der Vergangenheit liegt —
// das Abo-Objekt existiert dann weiterhin, ist aber fachlich nicht mehr aktiv.
export type SubscriptionStatus = 'active' | 'expired' | 'past_due' | 'canceled'

export type Subscription = {
  status: SubscriptionStatus
  current_period_end: string
  plan_id: string
  payment_method: PaymentMethod | null
  cancel_at_period_end: boolean
  // Premium-Checkout-Redesign: BE liefert diese Felder künftig für die
  // Restwert-Anzeige (planMath.remainingValue nutzt dieselbe Konvention).
  days_remaining?: number
  remaining_value?: number
  // additiv — true solange nicht gekündigt; Krypto-/Legacy-Premium ohne Stripe-Abo: immer false.
  auto_renew?: boolean
}

// Ergebnis von subscribe(): entweder sofort abgeschlossen oder 3DS/SCA erforderlich.
// Bei requiresAction muss der Client mit stripe.confirmCardPayment(clientSecret) abschließen.
export type SubscribeResult =
  | { requiresAction: false }
  | { requiresAction: true; clientSecret: string }

type UseSubscriptionResult = {
  subscription: Subscription | null | undefined
  isLoading: boolean
  isError: boolean
  isMutating: boolean
  mutationError: string | null
  refetch: () => Promise<{ data: Subscription | null | undefined }>

  subscribe: (paymentMethodId: string, planId: string, durationMonths: number) => Promise<SubscribeResult>
  cancel: () => Promise<void>
  resume: () => Promise<void>
  changePlan: (planId: string, durationMonths: number) => Promise<void>
  updatePaymentMethod: (stripeToken: string) => Promise<void>
  requestCryptoQuote: (planId: string, durationMonths: number) => Promise<CryptoQuote>
}

const QUERY_KEY = ['subscription', 'current']

/**
 * FE-ROB-04: Typisierter Sonderfall "User hat bereits ein aktives Abo" (HTTP 409).
 *
 * Frueher wurde dafuer `new Error('ALREADY_SUBSCRIBED')` geworfen und im Modal
 * per Message-String verglichen — was nie griff, weil apiClient stattdessen
 * eine ApiError mit der Message "API Error: 409" warf.
 */
export class AlreadySubscribedError extends Error {
  constructor() {
    super('ALREADY_SUBSCRIBED')
    this.name = 'AlreadySubscribedError'
  }
}

/**
 * FE-ROB-04: client_secret aus einer 402-ApiError herausziehen.
 * Das BE (subscriptions_controller, rescue Pay::ActionRequired) liefert
 * `{ requires_action: true, client_secret: "pi_..._secret_..." }` — je nach
 * Serializer flach oder unter `error`.
 */
function extractClientSecret(err: unknown): string | null {
  const body = err instanceof ApiError ? err.body : undefined
  if (!body || typeof body !== 'object') return null

  const direct = (body as { client_secret?: unknown }).client_secret
  if (typeof direct === 'string' && direct.length > 0) return direct

  const nested = (body as { error?: unknown }).error
  if (nested && typeof nested === 'object') {
    const value = (nested as { client_secret?: unknown }).client_secret
    if (typeof value === 'string' && value.length > 0) return value
  }
  return null
}

// Minimaler Typ für apiClient-Methoden, die kein Pfad-Schema kennen (z. B. crypto_quote).
// Wird nur an der einen Aufrufstelle verwendet — kein globales `as any`.
type UntypedPost = (url: string, opts: { body: Record<string, unknown> }) => Promise<{ data?: unknown; error?: unknown }>

export function useSubscription(): UseSubscriptionResult {
  const queryClient = useQueryClient()
  const [isMutating, setIsMutating] = useState(false)
  const [mutationError, setMutationError] = useState<string | null>(null)

  const query = useQuery<Subscription | null>({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const result = await apiClient.GET('/api/v2/subscription')
      if (result.response?.status === 401 || result.response?.status === 404) {
        return null
      }
      if (result.error) throw result.error
      const body = (result as unknown as { data?: { subscription?: Subscription | null } }).data
      return body?.subscription ?? null
    },
    retry: false,
  })

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEY })
  }, [queryClient])

  const subscribe = useCallback(
    async (paymentMethodId: string, planId: string, durationMonths: number): Promise<SubscribeResult> => {
      setIsMutating(true)
      setMutationError(null)
      try {
        // FE-ROB-04: apiClient wirft bei HTTP >= 400 eine ApiError, BEVOR hier
        // ein Status geprueft werden koennte. Die frueheren
        // `result.response?.status === 402/409`-Zweige waren damit toter Code:
        // jede SCA-pflichtige Karte (402 + client_secret) endete in
        // "Zahlungsfehler", der PaymentIntent blieb in requires_action haengen
        // und Premium wurde nie aktiviert. Deshalb werden 402/409 jetzt im
        // catch aus der ApiError heraus differenziert behandelt.
        let result: Awaited<ReturnType<typeof apiClient.POST>>
        try {
          result = await apiClient.POST('/api/v2/subscription', {
            body: { stripe_token: paymentMethodId, plan_id: planId, duration_months: durationMonths },
          })
        } catch (err: unknown) {
          const status = apiErrorStatus(err)

          // 409: Der User hat bereits ein aktives Abo — Sonderfluss im Modal.
          if (status === 409) {
            throw new AlreadySubscribedError()
          }

          // 402 mit client_secret: 3DS/SCA-Challenge. FE schliesst per
          // confirmCardPayment ab; die Premium-Aktivierung folgt serverseitig
          // per Webhook (idempotent).
          if (status === 402) {
            const clientSecret = extractClientSecret(err)
            if (clientSecret) {
              return { requiresAction: true, clientSecret }
            }
          }

          throw err
        }

        // Zweiter Weg fuer denselben Fall: liefert der Client die Antwort
        // (statt zu werfen), greifen dieselben Regeln — so bleibt das Handling
        // unabhaengig davon, ob eine Middleware die ApiError erzeugt hat.
        if (result.response?.status === 409) {
          throw new AlreadySubscribedError()
        }
        if (result.response?.status === 402) {
          const clientSecret = extractClientSecret(
            new ApiError(402, result.error ?? result.data, undefined),
          )
          if (clientSecret) {
            return { requiresAction: true, clientSecret }
          }
        }

        if (result.error) throw result.error
        invalidate()
        return { requiresAction: false }
      } finally {
        setIsMutating(false)
      }
    },
    [invalidate],
  )

  const cancel = useCallback(async () => {
    setIsMutating(true)
    setMutationError(null)
    try {
      const result = await apiClient.DELETE('/api/v2/subscription')
      if (result.error) throw result.error
      invalidate()
    } catch (err) {
      setMutationError('Kündigung fehlgeschlagen. Bitte erneut versuchen.')
      throw err
    } finally {
      setIsMutating(false)
    }
  }, [invalidate])

  // Premium-Checkout-Redesign: Kündigung (cancel_at_period_end) rückgängig machen.
  const resume = useCallback(async () => {
    setIsMutating(true)
    setMutationError(null)
    try {
      const untypedPost = apiClient.POST as unknown as UntypedPost
      const result = await untypedPost('/api/v2/subscription/resume', { body: {} })
      if (result.error) throw result.error
      invalidate()
    } catch (err) {
      setMutationError('Fortsetzen fehlgeschlagen. Bitte erneut versuchen.')
      throw err
    } finally {
      setIsMutating(false)
    }
  }, [invalidate])

  const changePlan = useCallback(
    async (planId: string, durationMonths: number) => {
      setIsMutating(true)
      setMutationError(null)
      try {
        const result = await apiClient.PATCH('/api/v2/subscription', {
          body: { plan_id: planId, duration_months: durationMonths },
        })
        if (result.error) throw result.error
        invalidate()
      } catch (err) {
        setMutationError('Plan-Wechsel fehlgeschlagen. Bitte erneut versuchen.')
        throw err
      } finally {
        setIsMutating(false)
      }
    },
    [invalidate],
  )

  const updatePaymentMethod = useCallback(
    async (stripeToken: string) => {
      setIsMutating(true)
      setMutationError(null)
      try {
        const result = await apiClient.PATCH('/api/v2/subscription/payment-method', {
          body: { stripe_token: stripeToken },
        })
        if (result.error) throw result.error
        invalidate()
      } catch (err) {
        setMutationError('Zahlungsmethode konnte nicht aktualisiert werden.')
        throw err
      } finally {
        setIsMutating(false)
      }
    },
    [invalidate],
  )

  // FIN-PAY-001 — Crypto-Quote anfordern (legt UpgradeCode an + liefert QR-Daten).
  // Reine Lese-/Vorbereitungs-Operation; Aktivierung erfolgt serverseitig per Cronjob,
  // sobald die On-Chain-Zahlung eingeht. FE pollt danach GET /api/v2/subscription (refetch).
  const requestCryptoQuote = useCallback(
    async (planId: string, durationMonths: number): Promise<CryptoQuote> => {
      setIsMutating(true)
      setMutationError(null)
      try {
        // /api/v2/subscription/crypto_quote ist nicht im generierten api-types-Schema —
        // gezielter Cast nur an dieser Stelle (kein globales `as any`).
        const untypedPost = apiClient.POST as unknown as UntypedPost
        const result = await untypedPost('/api/v2/subscription/crypto_quote', {
          body: { plan_id: planId, duration_months: durationMonths },
        })
        if (result.error) throw result.error
        return result.data as CryptoQuote
      } catch (err) {
        setMutationError('Crypto-Zahlungsdaten konnten nicht erstellt werden.')
        throw err
      } finally {
        setIsMutating(false)
      }
    },
    [],
  )

  return {
    subscription: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    isMutating,
    mutationError,
    refetch: () => query.refetch(),
    subscribe,
    cancel,
    resume,
    changePlan,
    updatePaymentMethod,
    requestCryptoQuote,
  }
}
